import { useId } from "react";

/**
 * MusaLoader — loader animado a partir del personaje de Musa.
 *
 * Props:
 *  - size      ancho en px (el alto se calcula solo). Default 160.
 *  - progress  0–100 para modo "determinado". Si no se pasa, la barra se llena en bucle.
 *  - label     texto debajo del personaje. `null` para ocultarlo. Default null (solo el personaje).
 *  - speed     duración del ciclo de la barra en segundos. Default 1.8.
 *  - className clase extra para el contenedor.
 *
 * Uso:
 *   <MusaLoader />
 *   <MusaLoader size={96} label={null} />
 *   <MusaLoader progress={uploadPct} label={`Subiendo ${uploadPct}%`} />
 */

const BAR_TRAVEL = 139.12; // distancia que recorre el relleno de la barra (unidades SVG)

const CSS = `
.musa-loader{display:inline-flex;flex-direction:column;align-items:center;gap:12px;color:#28082a}
.musa-loader svg{display:block;overflow:visible}
.musa-loader__label{font:600 14px/1.2 inherit;font-family:inherit;letter-spacing:.02em}
.musa-loader .ml-col{animation:ml-bob calc(var(--ml-speed) / 2) ease-in-out infinite}
.musa-loader .ml-col--r{animation-delay:calc(var(--ml-speed) / -4)}
.musa-loader .ml-eye{transform-box:fill-box;transform-origin:center;animation:ml-blink calc(var(--ml-speed) * 2) ease-in-out infinite}
.musa-loader .ml-eye--r{animation-delay:.08s}
.musa-loader .ml-fill{animation:ml-fill var(--ml-speed) cubic-bezier(.65,0,.35,1) infinite}
.musa-loader .ml-fill--static{animation:none;transition:transform .35s ease}
@keyframes ml-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
@keyframes ml-blink{0%,90%,100%{transform:scaleY(1)}94%{transform:scaleY(.12)}}
@keyframes ml-fill{
  0%{transform:translateX(-${BAR_TRAVEL}px);opacity:1}
  72%{transform:translateX(0);opacity:1}
  88%{transform:translateX(0);opacity:1}
  100%{transform:translateX(0);opacity:0}
}
@media (prefers-reduced-motion:reduce){
  .musa-loader .ml-col,.musa-loader .ml-eye{animation:none}
  .musa-loader .ml-fill{animation-duration:calc(var(--ml-speed) * 2)}
}
`;

/** @param {{size?: number, progress?: number, label?: string | null, speed?: number, className?: string}} props */
export default function MusaLoader({
  size = 160,
  progress,
  label = null,
  speed = 1.8,
  className = "",
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const bodyClip = `ml-body-${uid}`;
  const barClip = `ml-bar-${uid}`;

  const determinate = typeof progress === "number";
  const pct = determinate ? Math.min(100, Math.max(0, progress)) : 0;
  const fillStyle = determinate
    ? { transform: `translateX(${-BAR_TRAVEL * (1 - pct / 100)}px)` }
    : undefined;

  return (
    <div
      className={`musa-loader ${className}`.trim()}
      style={{ "--ml-speed": `${speed}s` }}
      role={determinate ? "progressbar" : "status"}
      aria-live="polite"
      aria-label={label || "Cargando"}
      {...(determinate && {
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-valuenow": Math.round(pct),
      })}
    >
      <style>{CSS}</style>
      <svg
        width={size}
        height={(size * 279.94) / 329.29}
        viewBox="0 0 329.29 279.94"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <clipPath id={bodyClip}>
            <rect y="-24.07" width="302.21" height="301.83" />
          </clipPath>
          <clipPath id={barClip}>
            <rect x="157.33" y="8.94" width="162.46" height="23.34" rx="11.67" />
          </clipPath>
        </defs>

        {/* Cuerpo */}
        <g clipPath={`url(#${bodyClip})`}>
          <g className="ml-col ml-col--l">
            <path fill="#ff5e46" d="M130.88,306.91V290.52a31.83,31.83,0,0,0,13.43,3,32.29,32.29,0,0,0,32.2-32.19V73.73a32.15,32.15,0,0,0-53.37-24.19h0l-.08.08a31.27,31.27,0,0,0-2.82,2.82L76.94,95.67h0A32.09,32.09,0,0,0,66.5,119.35V306.91a32.29,32.29,0,0,0,32.19,32.2h0A32.29,32.29,0,0,0,130.88,306.91Z" />
            <rect fill="#fff" x="-15.6" y="193.83" width="227.68" height="44.13" rx="22.07" transform="translate(-117.65 314.13) rotate(-90)" />
          </g>
          <g className="ml-col ml-col--r">
            <path fill="#ff5e46" d="M231.17,327.76v-16.4a31.79,31.79,0,0,0,13.43,3,32.28,32.28,0,0,0,32.19-32.19V94.57a32.14,32.14,0,0,0-53.36-24.19h0l-.08.08a31.27,31.27,0,0,0-2.82,2.82l-43.29,43.24h0a32.13,32.13,0,0,0-10.44,23.67V327.76A32.29,32.29,0,0,0,199,360h0A32.29,32.29,0,0,0,231.17,327.76Z" />
            <rect fill="#fff" x="86.01" y="213.34" width="225.03" height="44.13" rx="22.07" transform="translate(-36.89 433.93) rotate(-90)" />
          </g>
          <rect fill="#ff5e46" x="221.65" y="226.08" width="69.87" height="51.62" />
          <rect fill="#ff5e46" x="19.48" y="99.17" width="56.2" height="178.6" />
          <rect fill="#ff5e46" x="4.74" y="99.17" width="70.93" height="51.62" />
        </g>

        {/* Ojos */}
        <g className="ml-col ml-col--l">
          <rect className="ml-eye" fill="#28082a" x="91.08" y="103.82" width="26.62" height="37.37" rx="13.31" />
        </g>
        <g className="ml-col ml-col--r">
          <rect className="ml-eye ml-eye--r" fill="#28082a" x="192.58" y="124.73" width="26.62" height="37.37" rx="13.31" />
        </g>

        {/* Barra de carga */}
        <rect x="151.11" y="3.66" width="174.9" height="33.9" rx="16.95" fill="none" stroke="#28082a" strokeWidth="3.84" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath={`url(#${barClip})`}>
          <rect
            className={determinate ? "ml-fill ml-fill--static" : "ml-fill"}
            style={fillStyle}
            fill="#28082a"
            x="157.33" y="8.94" width="162.46" height="23.34" rx="11.67"
          />
        </g>
      </svg>
      {label && <span className="musa-loader__label">{label}</span>}
    </div>
  );
}
